// What the side pane opens (ui/SidePane.tsx): each kind of record's name in the trail, its editor, its usual width and
// its own page. A new kind is one entry here; links to it are PaneLinks with its kind, and a list page opens its rows
// as one with ListPage's `pane`.
import { useBillIndex } from "./lib/bills.ts";
import { useRow } from "./lib/sync.ts";
import { LinePane, LineTitle } from "./pages/BomEditor.tsx";
import { BillPanel } from "./pages/bills/BillPanel.tsx";
import { ItemPanel, itemPath } from "./pages/ItemsPage.tsx";
import { poPath } from "./pages/po/lib.ts";
import { PoPanel } from "./pages/po/PoPanel.tsx";
import { PoName } from "./pages/po/PoType.tsx";
import { ChangeNoticePanel } from "./pages/specs/ChangeNotices.tsx";
import { SpecPane, SpecTitle } from "./pages/specs/SpecWorkspace.tsx";
import { UserPanel } from "./pages/UsersPage.tsx";
import { VendorPanel } from "./pages/VendorsPage.tsx";
import { type PaneKind, paneBack } from "./ui/SidePane.tsx";

function ItemTitle({ id }: { id: string }) {
  const item = useRow("item", id);
  return item ? item.sku || item.name : "Item";
}
function VendorTitle({ id }: { id: string }) {
  const vendor = useRow("vendor", id);
  return vendor ? vendor.short_name || vendor.name : "Vendor";
}
function PoTitle({ id }: { id: string }) {
  const po = useRow("po", id);
  const vendor = useRow("vendor", po?.vendor_id);
  if (!po) return "PO";
  return <PoName po={po} vendor={vendor} />;
}
function BillTitle({ id }: { id: string }) {
  return useBillIndex().billById.get(id)?.number ?? "Bill";
}
function UserTitle({ id }: { id: string }) {
  const user = useRow("app_user", id);
  return user ? user.name || user.email : "User";
}
function NoticeTitle({ id }: { id: string }) {
  return useRow("change_notice", id)?.number ?? "Change notice";
}

export const PANE_KINDS: Record<string, PaneKind> = {
  line: { width: 420, Title: LineTitle, Body: LinePane },
  item: {
    width: 480,
    Title: ItemTitle,
    Body: ({ id }) => <ItemPanel id={id} close={paneBack} />,
    usePage: (id) => {
      const item = useRow("item", id);
      return item ? itemPath(item) : null;
    },
  },
  vendor: {
    width: 480,
    Title: VendorTitle,
    Body: ({ id }) => <VendorPanel id={id} close={paneBack} />,
    usePage: (id) => `/vendors?open=${id}`,
  },
  // A PO's lines need about 1000 px: beside a narrower list it floats over it
  po: { width: 1040, Title: PoTitle, Body: ({ id }) => <PoPanel key={id} id={id} close={paneBack} />, usePage: poPath },
  bill: { width: 680, Title: BillTitle, Body: ({ id }) => <BillPanel key={id} id={id} close={paneBack} />, usePage: (id) => `/bills?tab=bills&bill=${id}` },
  user: { width: 440, Title: UserTitle, Body: ({ id }) => <UserPanel id={id} close={paneBack} />, usePage: (id) => `/settings/users?open=${id}` },
  change_notice: {
    width: 520,
    Title: NoticeTitle,
    Body: ({ id }) => <ChangeNoticePanel id={id} close={paneBack} />,
    usePage: (id) => `/specs?tab=ecn&open=${id}`,
  },
  // A spec by its item's id: the full editor wants room to write, so beside a narrower page it floats over it
  spec: { width: 900, Title: SpecTitle, Body: ({ id }) => <SpecPane itemId={id} />, usePage: (id) => `/specs?open=${id}` },
};
